import { useCallback, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/**
 * Render a DOM element to a PDF Blob. Async (uses html2canvas), heavy work
 * is done here — the caller can then save the blob synchronously on click.
 */
async function renderElementToPDFBlob(element, { scale = 2, timeoutMs = 90000 } = {}) {
  if (!element) return null;

  const wasHidden = element.classList.contains("hidden");
  const originalCssText = element.style.cssText;

  if (wasHidden) element.classList.remove("hidden");
  element.style.position = "fixed";
  element.style.left = "-10000px";
  element.style.top = "0";
  element.style.width = "794px"; // ~A4 width in CSS px at 96 DPI
  element.style.background = "white";
  element.style.zIndex = "-1";
  element.style.display = "block";

  try {
    // Race html2canvas against a hard timeout so the button can't hang
    // silently on slow/low-memory devices.
    const canvas = await Promise.race([
      html2canvas(element, {
        scale,
        backgroundColor: "#ffffff",
        useCORS: true,
        windowWidth: 794,
        imageTimeout: 15000,
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("PDF render timed out")), timeoutMs)
      ),
    ]);

    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    const imgData = canvas.toDataURL("image/jpeg", 0.92);

    pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf.output("blob");
  } finally {
    element.style.cssText = originalCssText;
    if (wasHidden) element.classList.add("hidden");
  }
}

/**
 * Trigger a save for a Blob. Uses the same synchronous anchor-click pattern
 * as `XLSX.writeFile`. When the app is running inside an iframe (e.g. the
 * Emergent chat preview) Safari blocks programmatic downloads outright, so
 * we fall back to `window.open(blobUrl)` which shows the PDF in a new tab
 * where the user can save it via the browser's built-in PDF viewer.
 */
function saveBlob(blob, filename) {
  const safeName = filename.replace(/[^a-z0-9-_.]/gi, "_");
  const finalName = safeName.endsWith(".pdf") ? safeName : `${safeName}.pdf`;
  const url = URL.createObjectURL(blob);

  const inIframe = (() => {
    try {
      return window.top !== window.self;
    } catch {
      return true; // cross-origin parent throws — definitely in an iframe
    }
  })();

  if (inIframe) {
    // Open the PDF in a new tab. Safari renders it with its native PDF
    // viewer, and the user can Cmd+S or click the download icon.
    const win = window.open(url, "_blank");
    if (!win) {
      // Pop-ups blocked — last-ditch: try an anchor click anyway.
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } else {
    // Direct download (normal browsers, deployed app at dslradvisor.com).
    const a = document.createElement("a");
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * React hook: pre-generates a PDF from an element and exposes a synchronous
 * `download` function for the button click.
 *
 * @param {React.RefObject<HTMLElement>} elementRef - ref to the printable element
 * @param {() => string} getFilename - function returning the current filename
 * @param {Array} deps - when any of these changes, the PDF is regenerated
 * @returns {{ ready: boolean, generating: boolean, download: () => void }}
 */
/**
 * Lazy variant: PDF is generated only when the user clicks the button.
 * Use this for heavy pages where eager pre-generation on mount makes the
 * button feel broken (stuck on "Generating..." for many seconds before the
 * user has even interacted).
 *
 * Trade-off: the click can no longer be 100% synchronous, so on iframed
 * Safari the popup blocker may suppress the new-tab open. We mitigate that
 * by synchronously opening a blank tab on click and rewriting its location
 * once the blob is ready.
 *
 * @returns {{ ready: boolean, generating: boolean, download: () => void }}
 *   `ready` is always true so the button is enabled immediately.
 */
export function usePDFExportLazy(elementRef, getFilename, options = {}) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(false);
  const filenameRef = useRef(getFilename);
  filenameRef.current = getFilename;
  // Heavy pages (many sections) render faster and use less memory at lower scale.
  const renderScale = options.scale ?? 1.5;

  const download = useCallback(async () => {
    if (!elementRef.current) return;
    setError(false);

    // Detect iframe up-front (must run synchronously inside the user gesture).
    const inIframe = (() => {
      try {
        return window.top !== window.self;
      } catch {
        return true;
      }
    })();

    // Pre-open a blank tab synchronously so the popup blocker doesn't fire
    // after the async work. We'll navigate it once the blob is ready.
    const preWin = inIframe ? window.open("", "_blank") : null;

    setGenerating(true);
    try {
      const blob = await renderElementToPDFBlob(elementRef.current, { scale: renderScale });
      if (!blob) {
        if (preWin) preWin.close();
        setError(true);
        return;
      }
      const name =
        (typeof filenameRef.current === "function"
          ? filenameRef.current()
          : filenameRef.current) || "dslr-advisor.pdf";
      const url = URL.createObjectURL(blob);
      const safeName = name.replace(/[^a-z0-9-_.]/gi, "_");
      const finalName = safeName.endsWith(".pdf") ? safeName : `${safeName}.pdf`;

      if (preWin) {
        // Iframe: hand the blob URL to the pre-opened tab.
        preWin.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[PDF export] failed:", err);
      if (preWin) preWin.close();
      setError(true);
    } finally {
      setGenerating(false);
    }
  }, [elementRef, renderScale]);

  return { ready: true, generating, error, download };
}

export function usePDFExport(elementRef, getFilename, deps = []) {
  const [blob, setBlob] = useState(null);
  const [generating, setGenerating] = useState(true);
  const filenameRef = useRef(getFilename);
  filenameRef.current = getFilename;

  // Debounce regeneration so rapid dep changes (e.g. budget slider) don't
  // thrash html2canvas.
  useEffect(() => {
    let cancelled = false;
    setGenerating(true);
    const t = setTimeout(async () => {
      if (!elementRef.current) {
        setGenerating(false);
        return;
      }
      try {
        const newBlob = await renderElementToPDFBlob(elementRef.current);
        if (!cancelled) setBlob(newBlob);
      } catch {
        if (!cancelled) setBlob(null);
      } finally {
        if (!cancelled) setGenerating(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const download = useCallback(() => {
    if (!blob) return;
    const name =
      (typeof filenameRef.current === "function"
        ? filenameRef.current()
        : filenameRef.current) || "dslr-advisor.pdf";
    saveBlob(blob, name);
  }, [blob]);

  return { ready: !!blob && !generating, generating, download };
}


/**
 * Build a multi-page US Letter (8.5x11) PDF from one or more already-rendered
 * 816px-wide sheet elements. Each element becomes one full page (contained,
 * never cropped). Used by the Task Cards cheat sheets.
 */
export async function buildLetterPDFBlob(elements, { scale = 2, timeoutMs = 120000, onProgress } = {}) {
  const pdf = new jsPDF({ unit: "mm", format: "letter", orientation: "portrait" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  let first = true;
  const total = elements.filter(Boolean).length;
  let done = 0;
  for (const el of elements) {
    if (!el) continue;
    const canvas = await Promise.race([
      html2canvas(el, { scale, backgroundColor: "#ffffff", useCORS: true, windowWidth: 816, imageTimeout: 15000 }),
      new Promise((_, reject) => setTimeout(() => reject(new Error("PDF render timed out")), timeoutMs)),
    ]);
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    if (!first) pdf.addPage();
    first = false;
    let w = pw;
    let h = (canvas.height * pw) / canvas.width;
    if (h > ph) {
      h = ph;
      w = (canvas.width * ph) / canvas.height;
    }
    const x = (pw - w) / 2;
    pdf.addImage(imgData, "JPEG", x, 0, w, h);
    done += 1;
    if (onProgress) onProgress(done, total);
    // Yield to the event loop so the UI (progress text) can paint between
    // heavy rasterizations, and memory can be reclaimed.
    await new Promise((r) => setTimeout(r, 0));
  }
  return pdf.output("blob");
}

/**
 * Deliver a PDF blob to the user. If `preWin` (a tab opened synchronously
 * during the click) is provided, navigate it to the blob (iframe-safe);
 * otherwise trigger a normal download.
 */
export function deliverPDFBlob(blob, filename, preWin) {
  const safe = (filename || "dslr-advisor").replace(/[^a-z0-9-_.]/gi, "_");
  const finalName = safe.endsWith(".pdf") ? safe : `${safe}.pdf`;
  const url = URL.createObjectURL(blob);
  if (preWin) {
    preWin.location.href = url;
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
